/* ═══════════════════════════════════════════════════════════════════════
   "Your page" — where a business actually changes its own page.

   Everything this edits already existed and could only be set by someone
   with database access, which is another way of saying it did not exist.

   ── What this screen is trying to prevent ──────────────────────────────
   A business filling every box.

   Give someone a page builder and they will write, and their customer
   arrives at a wall of prose with the useful thing somewhere under it.
   So: no section is required, the counters read down rather than up, and
   the help text says what a thing is FOR rather than inviting copy. The
   conversation is shown first in the preview for the same reason it is
   first on the page.

   ── Two kinds of writing, and the difference matters ───────────────────
   What goes on the page is for the reader. What crAIg KNOWS is separate,
   and lives under "What he answers" — because page prose never reaches
   the model, deliberately. Somebody writing "we're closed Mondays" here
   and expecting crAIg to say it would be disappointed, so the screen says
   so where they would otherwise find out the hard way.
   ═══════════════════════════════════════════════════════════════════════ */

import { useCallback, useEffect, useState } from "react";
import { applyAccent, applyTheme, prefersDark, type Theme } from "./theme";
import { CraigFace } from "./components/CraigFace";
import { DashboardLink } from "./components/chrome";
import { LetCraigDoIt, Wordmark } from "./components/Brand";

interface Scheme {
  id: string;
  label: string;
  accent: string;
}
interface Face {
  id: string;
  label: string;
  hint: string;
}
interface Branding {
  accent?: string;
  scheme?: string;
  logoUrl?: string;
  tagline?: string;
  face?: "craig" | "brand";
  chat?: { surface?: string; font?: string; bubbles?: "accent" | "quiet" };
}

interface Named {
  id: string;
  label: string;
  hint?: string;
  tint?: string;
}

type Block =
  | { kind: "about"; title?: string; body: string }
  | { kind: "services"; title?: string; items: { label: string; body?: string }[] }
  | { kind: "facts"; title?: string; rows: { label: string; value: string }[] }
  | { kind: "questions"; title?: string; items: { q: string; a: string }[] }
  | { kind: "notice"; body: string };

interface Config {
  page?: { headline?: string; intro?: string; blocks?: Block[] };
  assistant?: { name?: string; greeting?: string; avatar?: string };
  [k: string]: unknown;
}

/** Fresh blocks, with the shape already right so nothing starts invalid. */
const BLANK: Record<Block["kind"], () => Block> = {
  about: () => ({ kind: "about", title: "About us", body: "" }),
  services: () => ({ kind: "services", title: "What we do", items: [{ label: "" }] }),
  facts: () => ({ kind: "facts", title: "Good to know", rows: [{ label: "", value: "" }] }),
  questions: () => ({ kind: "questions", title: "Questions people ask", items: [{ q: "", a: "" }] }),
  notice: () => ({ kind: "notice", body: "" }),
};

const KINDS: { kind: Block["kind"]; label: string; hint: string }[] = [
  { kind: "facts", label: "Facts", hint: "Hours, area, licence number. One line each." },
  { kind: "services", label: "What you do", hint: "A few words each, not a brochure." },
  { kind: "questions", label: "Questions", hint: "The ones you answer on the phone daily." },
  { kind: "about", label: "About you", hint: "A short paragraph. The rest folds away." },
  { kind: "notice", label: "Notice", hint: "A temporary line at the top. Closed until the 5th." },
];

export function PageEditor({ slug }: { slug: string }) {
  /* Which page is being edited. Passed in rather than looked up: the
     account may own several and the URL is what says which. */

  const [theme, setTheme] = useState<Theme>(() => (prefersDark() ? "dark" : "light"));
  const [branding, setBranding] = useState<Branding>({});
  const [config, setConfig] = useState<Config>({});
  const [schemes, setSchemes] = useState<Scheme[]>([]);
  const [faces, setFaces] = useState<Face[]>([]);
  const [surfaces, setSurfaces] = useState<Named[]>([]);
  const [fonts, setFonts] = useState<Named[]>([]);
  const [bubbles, setBubbles] = useState<Named[]>([]);
  const [preview, setPreview] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    applyTheme(theme);
    applyAccent(branding.accent ?? schemes.find((s) => s.id === branding.scheme)?.accent, theme);
  }, [theme, branding, schemes]);

  useEffect(() => {
    Promise.all([
      fetch(`/api/dash/${slug}/looks`).then((r) => (r.ok ? r.json() : Promise.reject(r.status))),
      fetch(`/api/dash/${slug}/config`).then((r) => (r.ok ? r.json() : Promise.reject(r.status))),
    ])
      .then(([l, c]) => {
        setSchemes(l.schemes);
        setFaces(l.faces);
        setSurfaces(l.surfaces ?? []);
        setFonts(l.fonts ?? []);
        setBubbles(l.bubbles ?? []);
        setBranding(l.branding ?? {});
        setPreview(l.preview);
        setConfig(c.config ?? {});
      })
      .catch((s) => {
        if (s === 401) window.location.href = "/account/sign-in";
        else setError("Couldn't load that just now.");
      })
      .finally(() => setLoading(false));
  }, []);

  const page = config.page ?? {};
  const blocks = page.blocks ?? [];

  const setPage = (patch: Partial<NonNullable<Config["page"]>>) =>
    setConfig((c) => ({ ...c, page: { ...(c.page ?? {}), ...patch } }));

  const setBlocks = (next: Block[]) => setPage({ blocks: next });
  const patchBlock = (i: number, patch: Partial<Block>) =>
    setBlocks(blocks.map((b, j) => (i === j ? ({ ...b, ...patch } as Block) : b)));

  const move = (i: number, by: number) => {
    const j = i + by;
    if (j < 0 || j >= blocks.length) return;
    const next = [...blocks];
    [next[i], next[j]] = [next[j]!, next[i]!];
    setBlocks(next);
  };

  const save = useCallback(async () => {
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      /* Two stores, because they are two different things: colours live on
         the tenant row and the page lives in the overlay. One button,
         because that distinction is ours and not the customer's problem. */
      for (const [url, body] of [
        [`/api/dash/${slug}/branding`, { branding }],
        [`/api/dash/${slug}/config`, { config }],
      ] as const) {
        const res = await fetch(url, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });
        if (!res.ok) {
          const b = (await res.json().catch(() => ({}))) as { error?: string };
          throw new Error(b.error ?? "That didn't save.");
        }
      }
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      setError(e instanceof Error ? e.message : "That didn't save.");
    } finally {
      setSaving(false);
    }
  }, [branding, config]);

  const uploadLogo = async (file: File) => {
    setError(null);
    const form = new FormData();
    form.append("file", file, file.name);
    const res = await fetch(`/api/dash/${slug}/logo`, { method: "POST", body: form });
    const body = (await res.json().catch(() => ({}))) as { logoUrl?: string; error?: string };
    if (!res.ok) return setError(body.error ?? "That didn't upload.");
    setBranding((b) => ({ ...b, logoUrl: body.logoUrl }));
  };

  if (loading) return <Centre>Loading…</Centre>;

  return (
    <div className="min-h-screen flex flex-col">
      <header style={{ borderBottom: "1px solid var(--line)", background: "var(--surface)" }}>
        <div className="max-w-3xl mx-auto px-5 py-3.5 flex items-center justify-between gap-3">
          {/* Back is one step — to this chat. Dashboard is all the way out,
              and it is on every one of these screens for the same reason:
              somebody deep in their own settings should never have to
              guess how many taps it takes to get home. */}
          <div className="flex items-center gap-2 shrink-0">
            <a href={`/dashboard/${slug}`} className="btn btn-quiet btn-term" style={{ padding: "0.35rem 0.6rem" }}>
              ← Back
            </a>
            <DashboardLink />
          </div>
          <span className="mono truncate" style={{ fontWeight: 650, fontSize: 14.5 }}>
            Your page
          </span>
          <button
            type="button"
            className="btn btn-quiet btn-term"
            onClick={() => setTheme((t) => (t === "dark" ? "light" : "dark"))}
            aria-label="Switch theme"
            style={{ padding: "0.3rem 0.55rem" }}
          >
            {theme === "dark" ? "☀" : "☾"}
          </button>
        </div>
      </header>

      <main className="flex-1 w-full max-w-3xl mx-auto px-5 py-6 space-y-4">
        {error && (
          <p
            role="alert"
            className="card px-4 py-3"
            style={{ fontSize: 14, color: "var(--danger)", background: "var(--warn-tint)", borderColor: "var(--warn)" }}
          >
            {error}
          </p>
        )}

        {/* ── Colour ─────────────────────────────────────────────────── */}
        <Panel title="Your colour" hint="Everything on your page is built from this one choice.">
          <div className="flex flex-wrap gap-2">
            {schemes.map((s) => {
              const on = branding.scheme === s.id && !branding.accent;
              return (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => setBranding((b) => ({ ...b, scheme: s.id, accent: undefined }))}
                  title={s.label}
                  aria-pressed={on}
                  aria-label={s.label}
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 10,
                    background: s.accent,
                    border: on ? "3px solid var(--ink)" : "1px solid var(--line)",
                    cursor: "pointer",
                  }}
                />
              );
            })}
          </div>
          <label className="block mt-3">
            <span className="term-label" style={{ display: "block", marginBottom: 4 }}>
              Or your own, if you know it
            </span>
            <input
              className="field"
              placeholder="#2e6f5e"
              value={branding.accent ?? ""}
              onChange={(e) =>
                setBranding((b) => ({ ...b, accent: e.target.value.trim() || undefined }))
              }
              style={{ maxWidth: 180 }}
            />
          </label>
        </Panel>

        {/* ── Logo ───────────────────────────────────────────────────── */}
        <Panel title="Your logo" hint="Optional. It sits above your name.">
          <div className="flex items-center gap-4 flex-wrap">
            {branding.logoUrl && (
              <img
                src={branding.logoUrl}
                alt=""
                style={{ maxHeight: 48, maxWidth: 160, border: "1px solid var(--line)", borderRadius: 6 }}
              />
            )}
            <label className="btn btn-quiet btn-term" style={{ cursor: "pointer" }}>
              {branding.logoUrl ? "Replace" : "Upload"}
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp,image/gif"
                hidden
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) void uploadLogo(f);
                }}
              />
            </label>
            {branding.logoUrl && (
              <button
                type="button"
                className="btn btn-quiet btn-term"
                onClick={() => setBranding((b) => ({ ...b, logoUrl: undefined }))}
              >
                Remove
              </button>
            )}
          </div>
        </Panel>

        {/* ── crAIg ──────────────────────────────────────────────────── */}
        <Panel title="Your assistant" hint="What your customers call him, and how he opens.">
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="His name here">
              <input
                className="field"
                placeholder="crAIg"
                maxLength={40}
                value={config.assistant?.name ?? ""}
                onChange={(e) =>
                  setConfig((c) => ({ ...c, assistant: { ...(c.assistant ?? {}), name: e.target.value } }))
                }
              />
            </Field>
            <Field label="An emoji, instead of his face">
              <input
                className="field"
                placeholder="🔧"
                maxLength={8}
                value={config.assistant?.avatar ?? ""}
                onChange={(e) =>
                  setConfig((c) => ({ ...c, assistant: { ...(c.assistant ?? {}), avatar: e.target.value } }))
                }
                style={{ maxWidth: 100 }}
              />
            </Field>
          </div>
          <Field label="His first line">
            <input
              className="field"
              placeholder="Tell me what's going on and I'll get it to the right person."
              maxLength={300}
              value={config.assistant?.greeting ?? ""}
              onChange={(e) =>
                setConfig((c) => ({ ...c, assistant: { ...(c.assistant ?? {}), greeting: e.target.value } }))
              }
            />
          </Field>

          <div className="mt-3">
            <span className="term-label" style={{ display: "block", marginBottom: 6 }}>
              His face
            </span>
            <div className="flex flex-wrap gap-2">
              {faces.map((f) => (
                <button
                  key={f.id}
                  type="button"
                  className={branding.face === f.id || (!branding.face && f.id === "craig") ? "btn btn-primary btn-term" : "btn btn-quiet btn-term"}
                  onClick={() => setBranding((b) => ({ ...b, face: f.id as "craig" | "brand" }))}
                  title={f.hint}
                >
                  {f.label}
                </button>
              ))}
              <span className="flex items-center gap-2 ml-1">
                <CraigFace size={30} brand={branding.face === "brand"} />
              </span>
            </div>
          </div>
        </Panel>

        {/* ── The conversation ───────────────────────────────────────── */}
        <Panel
          title="The conversation"
          hint="How the chat itself looks. Backgrounds are tints of your theme and fonts fall back to the multilingual set, so none of these can make it hard to read."
        >
          <Choice
            label="Background"
            options={surfaces}
            value={branding.chat?.surface ?? "plain"}
            onPick={(id) => setBranding((b) => ({ ...b, chat: { ...(b.chat ?? {}), surface: id } }))}
            swatch
          />
          <Choice
            label="Font"
            options={fonts}
            value={branding.chat?.font ?? "system"}
            onPick={(id) => setBranding((b) => ({ ...b, chat: { ...(b.chat ?? {}), font: id } }))}
          />
          <Choice
            label="Their messages"
            options={bubbles}
            value={branding.chat?.bubbles ?? "accent"}
            onPick={(id) =>
              setBranding((b) => ({
                ...b,
                chat: { ...(b.chat ?? {}), bubbles: id as "accent" | "quiet" },
              }))
            }
          />
        </Panel>

        {/* ── The page ───────────────────────────────────────────────── */}
        <Panel
          title="Your page"
          hint="Short is better. Your customers came to get something done, not to read."
        >
          <Field label="Headline">
            <input
              className="field"
              placeholder="Riverside Trades"
              maxLength={120}
              value={page.headline ?? ""}
              onChange={(e) => setPage({ headline: e.target.value })}
            />
          </Field>
          <Field label="One line under it">
            <textarea
              className="field"
              rows={2}
              maxLength={400}
              placeholder="Plumbing and gas across the northern suburbs."
              value={page.intro ?? ""}
              onChange={(e) => setPage({ intro: e.target.value })}
            />
          </Field>
          <p style={{ fontSize: 12.5, color: "var(--ink-3)", marginTop: -4 }}>
            {400 - (page.intro ?? "").length} characters left
          </p>
        </Panel>

        {/* ── Sections ───────────────────────────────────────────────── */}
        <Panel
          title="Sections"
          hint="All optional, and they sit under the conversation. Add only what someone would actually ask."
        >
          <div className="space-y-3">
            {blocks.map((b, i) => (
              <BlockEditor
                key={i}
                block={b}
                first={i === 0}
                last={i === blocks.length - 1}
                onChange={(patch) => patchBlock(i, patch)}
                onRemove={() => setBlocks(blocks.filter((_, j) => j !== i))}
                onUp={() => move(i, -1)}
                onDown={() => move(i, 1)}
              />
            ))}
          </div>

          {blocks.length < 12 && (
            <div className="flex flex-wrap gap-2 mt-3">
              {KINDS.map((k) => (
                <button
                  key={k.kind}
                  type="button"
                  className="btn btn-quiet btn-term"
                  title={k.hint}
                  onClick={() => setBlocks([...blocks, BLANK[k.kind]()])}
                >
                  + {k.label}
                </button>
              ))}
            </div>
          )}
        </Panel>

        <div
          className="flex items-center gap-3 flex-wrap"
          style={{ position: "sticky", bottom: 0, background: "var(--bg)", padding: "12px 0" }}
        >
          <button type="button" className="btn btn-primary btn-term" onClick={save} disabled={saving}>
            {saving ? "Saving…" : "Save"}
          </button>
          <a href={preview} target="_blank" rel="noreferrer" className="btn btn-quiet btn-term">
            See it as a customer
          </a>
          {saved && <span style={{ fontSize: 13.5, color: "var(--ok)" }}>Saved.</span>}
        </div>

        <p style={{ fontSize: 13, color: "var(--ink-3)", lineHeight: 1.55 }}>
          Nothing written here is read by crAIg — this is what your customers see. What he{" "}
          <em>knows</em> (your prices, your hours, what you can't help with) is set under{" "}
          <strong>What he asks</strong>, so the two can't get muddled.
        </p>
      </main>

      <LetCraigDoIt />
    </div>
  );
}

/* ── Blocks ─────────────────────────────────────────────────────────────── */

function BlockEditor({
  block,
  first,
  last,
  onChange,
  onRemove,
  onUp,
  onDown,
}: {
  block: Block;
  first: boolean;
  last: boolean;
  onChange(patch: Partial<Block>): void;
  onRemove(): void;
  onUp(): void;
  onDown(): void;
}) {
  const kind = KINDS.find((k) => k.kind === block.kind);
  return (
    <div className="panel px-4 py-3.5">
      <div className="flex items-center justify-between gap-2 mb-2.5">
        <span className="term-label">{kind?.label ?? block.kind}</span>
        <span className="flex gap-1">
          <Tiny onClick={onUp} disabled={first} label="Move up">
            ↑
          </Tiny>
          <Tiny onClick={onDown} disabled={last} label="Move down">
            ↓
          </Tiny>
          <Tiny onClick={onRemove} label="Remove">
            ✕
          </Tiny>
        </span>
      </div>

      {block.kind !== "notice" && (
        <Field label="Heading">
          <input
            className="field"
            maxLength={80}
            value={block.title ?? ""}
            onChange={(e) => onChange({ title: e.target.value } as Partial<Block>)}
          />
        </Field>
      )}

      {(block.kind === "about" || block.kind === "notice") && (
        <Field label={block.kind === "notice" ? "The notice" : "About you"}>
          <textarea
            className="field"
            rows={block.kind === "notice" ? 2 : 4}
            maxLength={2000}
            placeholder={
              block.kind === "notice"
                ? "Closed until the 5th of January."
                : "Family run since 1998. Leave a blank line between paragraphs — only the first shows, the rest folds away."
            }
            value={block.body}
            onChange={(e) => onChange({ body: e.target.value } as Partial<Block>)}
          />
        </Field>
      )}

      {block.kind === "services" && (
        <Rows
          rows={block.items}
          onChange={(items) => onChange({ items } as Partial<Block>)}
          blank={{ label: "" }}
          render={(it, set) => (
            <input
              className="field"
              placeholder="Blocked drains"
              maxLength={80}
              value={it.label}
              onChange={(e) => set({ label: e.target.value })}
            />
          )}
        />
      )}

      {block.kind === "facts" && (
        <Rows
          rows={block.rows}
          onChange={(rows) => onChange({ rows } as Partial<Block>)}
          blank={{ label: "", value: "" }}
          render={(r, set) => (
            <div className="grid gap-2" style={{ gridTemplateColumns: "1fr 1.4fr" }}>
              <input
                className="field"
                placeholder="Hours"
                maxLength={80}
                value={r.label}
                onChange={(e) => set({ label: e.target.value })}
              />
              <input
                className="field"
                placeholder="Mon–Fri 7am–4pm"
                maxLength={200}
                value={r.value}
                onChange={(e) => set({ value: e.target.value })}
              />
            </div>
          )}
        />
      )}

      {block.kind === "questions" && (
        <Rows
          rows={block.items}
          onChange={(items) => onChange({ items } as Partial<Block>)}
          blank={{ q: "", a: "" }}
          render={(it, set) => (
            <div className="space-y-1.5">
              <input
                className="field"
                placeholder="Do you charge to quote?"
                maxLength={80}
                value={it.q}
                onChange={(e) => set({ q: e.target.value })}
              />
              <textarea
                className="field"
                rows={2}
                maxLength={2000}
                placeholder="No — we'll look and quote for nothing."
                value={it.a}
                onChange={(e) => set({ a: e.target.value })}
              />
            </div>
          )}
        />
      )}
    </div>
  );
}

/** A repeating list with add and remove, for the three blocks that need one. */
function Rows<T>({
  rows,
  onChange,
  blank,
  render,
}: {
  rows: T[];
  onChange(next: T[]): void;
  blank: T;
  render(row: T, set: (patch: Partial<T>) => void): React.ReactNode;
}) {
  return (
    <div className="space-y-2">
      {rows.map((r, i) => (
        <div key={i} className="flex gap-2 items-start">
          <div className="flex-1 min-w-0">
            {render(r, (patch) => onChange(rows.map((x, j) => (i === j ? { ...x, ...patch } : x))))}
          </div>
          <Tiny onClick={() => onChange(rows.filter((_, j) => j !== i))} label="Remove">
            ✕
          </Tiny>
        </div>
      ))}
      {rows.length < 24 && (
        <button
          type="button"
          className="btn btn-quiet btn-term"
          style={{ padding: "0.3rem 0.7rem", fontSize: 13 }}
          onClick={() => onChange([...rows, { ...blank }])}
        >
          + Add
        </button>
      )}
    </div>
  );
}

/* ── Furniture ──────────────────────────────────────────────────────────── */

function Panel({ title, hint, children }: { title: string; hint?: string; children: React.ReactNode }) {
  return (
    <section className="panel panel-ticks px-5 py-5">
      <div className="term-label">{title}</div>
      {hint && (
        <p style={{ fontSize: 13, color: "var(--ink-3)", margin: "4px 0 12px", lineHeight: 1.5 }}>
          {hint}
        </p>
      )}
      {children}
    </section>
  );
}

/** A row of named options. One choice, always one selected. */
function Choice({
  label,
  options,
  value,
  onPick,
  swatch,
}: {
  label: string;
  options: Named[];
  value: string;
  onPick(id: string): void;
  swatch?: boolean;
}) {
  if (!options.length) return null;
  return (
    <div className="mt-3">
      <span className="term-label" style={{ display: "block", marginBottom: 6 }}>
        {label}
      </span>
      <div className="flex flex-wrap gap-2">
        {options.map((o) => (
          <button
            key={o.id}
            type="button"
            title={o.hint}
            aria-pressed={value === o.id}
            className={value === o.id ? "btn btn-primary btn-term" : "btn btn-quiet btn-term"}
            onClick={() => onPick(o.id)}
          >
            {swatch && o.tint && (
              <span
                aria-hidden="true"
                style={{
                  display: "inline-block",
                  width: 10,
                  height: 10,
                  borderRadius: 3,
                  background: o.tint,
                  marginRight: 6,
                }}
              />
            )}
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block mt-2.5">
      <span className="term-label" style={{ display: "block", marginBottom: 4 }}>
        {label}
      </span>
      {children}
    </label>
  );
}

function Tiny({
  children,
  onClick,
  disabled,
  label,
}: {
  children: React.ReactNode;
  onClick(): void;
  disabled?: boolean;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
      className="btn btn-quiet"
      style={{ padding: "0.2rem 0.5rem", fontSize: 12, opacity: disabled ? 0.35 : 1 }}
    >
      {children}
    </button>
  );
}

function Centre({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen grid place-items-center px-5">
      <div className="flex flex-col items-center gap-3">
        <CraigFace size={40} thinking />
        <span style={{ fontSize: 14, color: "var(--ink-3)" }}>{children}</span>
        <Wordmark size={12} muted />
      </div>
    </div>
  );
}
